<template>
  <Teleport :to="portalRootElementSelector">
    <ModalRoot
      v-model="isOpen"
      prevent-close-on-backdrop-click
      class="!z-[10000000]"
      :aria-label="t('channel.auth.phone.title')"
    >
      <template #body="{ close }">
        <LoginModalBody
          :is-open="isOpen"
          :show-back-button="step !== 'phoneNumber'"
          :title="modalTitle"
          :caption="modalCaption"
          :is-referral-invitation="!!referrer"
          @close="onClose(close)"
          @go-back="onGoBack"
        >
          <ReferralInvitation v-if="referrer" :referrer="referrer" />

          <LoginPhoneForm
            v-model:step="step"
            :loading="loading"
            :error="error"
            :countdown-seconds="countdownSeconds"
            @submit-phone-number="onSubmitPhoneNumber"
            @submit-otp="onSubmitOtp"
          >
            <template #authOptions>
              <slot name="authOptions" />
            </template>
          </LoginPhoneForm>
        </LoginModalBody>
        <div id="captcha-container"></div>
      </template>
    </ModalRoot>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import LoginModalBody from "./login-modal-body.vue";
import LoginPhoneForm from "./login-phone-form.vue";
import type { User } from "@/generated/graphql";
import { useI18n } from "vue-i18n";
import { portalRootElementSelector } from "@/constants/ui";
import { useSocketsLazyEvents } from "@/composables/ably/use-sockets-lazy-events";
import { useAppStore } from "@/stores/app.store";
import ReferralInvitation from "./referral-invitation.vue";
import { useMaybeWalletService } from "@/services/wallet/wallet.service";

interface TwilioStatusUpdate {
  type: string;
  messageSid: string;
  errorCode: string;
  errorMessage?: string;
  appConnectionId: string;
}

const appStore = useAppStore();

const isOpen = defineModel<boolean>("isOpen", { required: true });
const step = defineModel<"phoneNumber" | "otp">("step", { required: true });

const emit = defineEmits<{
  submitPhoneNumber: [fullPhoneNumber: string];
  submitOtp: [{ fullPhoneNumber: string; otp: string }];
  validate: [];
}>();

const props = defineProps<{
  loading: boolean;
  error: string | null;
  referrer?: User;
  countdownSeconds?: number;
}>();

const { t } = useI18n();

const phoneNumberForOtp = ref<string | null>(null);

const smsNotificationMessage = ref("");

useSocketsLazyEvents<TwilioStatusUpdate>({
  // Per-connection channel: only this client's OTP status arrives here.
  // The reactive channelName getter re-subscribes once connectionId is known.
  channelName: () => `api-events:otp-status:${appStore.connectionId}`,
  // Only attach while the user is actually in the OTP step of the auth flow;
  // detaches as soon as the modal closes or returns to the phone-number step.
  enabled: computed(() => isOpen.value && step.value === "otp"),
  listener(msg) {
    // Defense-in-depth: verify the message targets this connection even
    // though the channel is already scoped per connectionId.
    if (
      msg.type === "sms-status-update" &&
      msg.appConnectionId === appStore.connectionId
    ) {
      smsNotificationMessage.value = msg.errorCode;
      const errorMessage = `OTP code could not be delivered. Error message: ${msg.errorMessage}. Message SID: ${msg.messageSid}`;

      console.error(
        "[LoginWithPhoneNumberModal] Error while sending OTP",
        errorMessage,
        msg.errorCode,
        msg.errorMessage,
        msg.messageSid,
        phoneNumberForOtp.value,
      );
    }
  },
  options: {
    rewind: 1,
  },
});

const modalTitle = computed(() =>
  step.value === "phoneNumber"
    ? t("channel.auth.phone.title")
    : t("channel.auth.phone.titleCode", { phone: phoneNumberForOtp.value }),
);

const walletService = useMaybeWalletService();
const modalCaption = computed(() => {
  if (step.value === "phoneNumber") {
    const token = walletService.value?.channelWebThreeTokens?.[0];
    if (token) {
      return t("channel.auth.phone.captionWebThree", {
        tokenLabel: token.label,
      });
    }

    return t("channel.auth.phone.caption");
  }

  return undefined;
});

function resetState() {
  step.value = "phoneNumber";
  phoneNumberForOtp.value = null;
  smsNotificationMessage.value = "";
}

watch(isOpen, (v) => {
  if (!v) resetState();
});

function onSubmitPhoneNumber(fullPhoneNumber: string) {
  phoneNumberForOtp.value = fullPhoneNumber;
  emit("submitPhoneNumber", fullPhoneNumber);
}

function onSubmitOtp(payload: { fullPhoneNumber: string; otp: string }) {
  emit("submitOtp", payload);
}

function onGoBack() {
  step.value = "phoneNumber";
  smsNotificationMessage.value = "";
}

function onClose(close: () => void) {
  const isCountdownActive =
    props.countdownSeconds && props.countdownSeconds > 0;
  if (step.value === "phoneNumber" && isCountdownActive) return;
  close();
}
</script>
